/**
 * strokeTrend — pure data model for the freestyle Glide trend card.
 *
 * Input: an array of per-swim stroke stats (from the future strokeStats.v1 cache
 * or inline sample data). Output: a TrendViewModel the StrokeTrendCard renders.
 *
 * Lower Glide = smoother and more efficient (lower SWOLF). This model is
 * direction-agnostic — the CHART renders direction by treating lower bars as
 * good via an explicit label in the component.
 *
 * Compatible with the planned `swimset.strokeStats.v1` cache shape.
 */

/** Per-stroke efficiency snapshot for one swim. */
export interface PerStrokeStats {
  glideAvg: number; // SWOLF average for this stroke's lengths
  meters: number;
  lengthCount: number;
  /** Avg strokes per length (the raw fact that replaces the fused Glide). */
  avgStrokes?: number;
  /** Avg seconds per length (secondary raw fact). */
  avgSeconds?: number;
}

/** Per-swim, per-stroke stats. Other stroke keys are optional for future use. */
export interface StrokeWorkoutStats {
  swimUuid: string;
  /** ISO date string "YYYY-MM-DD" */
  swimDate: string;
  /** Convenience alias for byStroke.Freestyle — kept for backward compat with
   *  view-model consumers (buildStrokeTrendViewModel reads `s.freestyle`). */
  freestyle?: PerStrokeStats;
  /** All available strokes for this swim. Keys match HealthKit stroke-style
   *  strings. The trend reads only Freestyle today; the rest are future-proofing. */
  byStroke?: {
    Freestyle?: PerStrokeStats;
    Breaststroke?: PerStrokeStats;
    Backstroke?: PerStrokeStats;
    Butterfly?: PerStrokeStats;
  };
}

/** One point in the per-swim series, for ECharts. */
export interface GlideSeriesPoint {
  swimDate: string;
  /** Kept for back-compat/internal; the bars now render strokesAvg. */
  glideAvg: number;
  /** Avg strokes per length for this swim (the bar height). */
  strokesAvg: number;
  /** Avg seconds per length for this swim (caption secondary). */
  secondsAvg: number;
}

/** Coverage state — indexed N of M swims. */
export interface TrendCoverage {
  /** Number of swims that have freestyle data (= length of `series`). */
  indexed: number;
  /** Total swims the trend could count (pool swims; open water excluded). */
  total: number;
  /**
   * True when indexed >= 50% of total AND indexed >= 5.
   * Below floor: the chart does NOT render.
   */
  meetsFloor: boolean;
  /**
   * Open-water swims excluded from `total` (the trend counts pool lengths, so
   * they can never join). Lets the card suppress "All 0 swims shown" / "more
   * freestyle" prompts when the swimmer's history is open-water-only.
   */
  owExcluded: number;
}

/** The view-model StrokeTrendCard consumes. All rendering decisions derived here. */
export interface TrendViewModel {
  /** Plain-English neutral sentence describing the takeaway. Never gamified. */
  takeawaySentence: string;
  /** Per-swim freestyle Glide series, sorted by swimDate ascending. */
  series: GlideSeriesPoint[];
  /** Overall average Glide (rounded whole number). */
  overallGlideAvg: number;
  /** Overall average strokes per length (rounded). The card's primary number. */
  overallStrokesAvg: number;
  /**
   * Fewest strokes per length across the indexed swims (rounded) — the
   * swimmer's own best. Used to give the average number a frame of reference
   * from their OWN history, not an external norms table. 0 when nothing indexed.
   */
  overallStrokesBest: number;
  /** Overall average seconds per length (rounded). The caption's secondary. */
  overallSecondsAvg: number;
  /** Coverage metadata, shown on every render. */
  coverage: TrendCoverage;
}

/**
 * Build the freestyle Glide trend view-model.
 *
 * @param stats           Per-swim stats array from the strokeStats cache (or sample data).
 * @param totalSwimCount  Total swim count from listWorkouts — used for the coverage line.
 */
export function buildStrokeTrendViewModel(
  stats: StrokeWorkoutStats[],
  totalSwimCount: number,
  openWaterSwimCount = 0,
): TrendViewModel {
  // Filter to swims that have freestyle data AND have been re-indexed with the
  // raw strokes/seconds facts. Cached strokeStats.v1 entries written before G9
  // carry only the old fused glideAvg with avgStrokes/avgSeconds undefined —
  // and setsDiscovered's appliedWorkoutIds means they never get re-indexed on
  // their own. Treat those as not-yet-indexed rather than coercing to 0, or
  // the trend fabricates a "0 strokes per length" claim.
  const withFreestyle = stats.filter(
    (s) => s.freestyle != null && s.freestyle.avgStrokes != null && s.freestyle.avgSeconds != null,
  );

  // Sort by date ascending for the chart
  const sorted = [...withFreestyle].sort((a, b) => a.swimDate.localeCompare(b.swimDate));

  const series: GlideSeriesPoint[] = sorted.map((s) => ({
    swimDate: s.swimDate,
    glideAvg: s.freestyle!.glideAvg,
    strokesAvg: s.freestyle!.avgStrokes!,
    secondsAvg: s.freestyle!.avgSeconds!,
  }));

  const indexed = series.length;
  // Clamp: displayed total can never be less than indexed so "N of M" never shows N > M.
  const total = Math.max(indexed, totalSwimCount);

  const meetsFloor = indexed >= 5 && indexed / Math.max(1, total) >= 0.5;

  const mean = (pick: (p: GlideSeriesPoint) => number) =>
    indexed === 0 ? 0 : Math.round(series.reduce((sum, p) => sum + pick(p), 0) / indexed);

  const overallGlideAvg = mean((p) => p.glideAvg);
  const overallStrokesAvg = mean((p) => p.strokesAvg);
  const overallSecondsAvg = mean((p) => p.secondsAvg);
  const overallStrokesBest =
    indexed === 0 ? 0 : Math.round(Math.min(...series.map((p) => p.strokesAvg)));

  const takeawaySentence = buildSentence(
    overallStrokesAvg,
    overallSecondsAvg,
    indexed,
    total,
    openWaterSwimCount,
  );

  return {
    takeawaySentence,
    series,
    overallGlideAvg,
    overallStrokesAvg,
    overallStrokesBest,
    overallSecondsAvg,
    coverage: { indexed, total, meetsFloor, owExcluded: openWaterSwimCount },
  };
}

/**
 * Own-data frame of reference for the average strokes-per-length number.
 *
 * Gives the card's primary number a scale from the swimmer's OWN history
 * (their smoothest swim), not an external norms table. Returns null when there
 * is no meaningful spread (best is not lower than the average) so the card
 * stays honestly simple instead of restating the same number twice.
 */
export function strokesFrameLine(vm: TrendViewModel): string | null {
  const { overallStrokesAvg, overallStrokesBest } = vm;
  if (overallStrokesBest <= 0 || overallStrokesBest >= overallStrokesAvg) return null;
  // "per 25": the glide counts 25 m swims only (StrokesTab), one term with the stroke cards.
  return `Your smoothest swim so far: ${overallStrokesBest} strokes per 25.`;
}

/**
 * Build the plain-English neutral takeaway sentence.
 * No gamification. No em dashes. No improvement framing.
 */
function buildSentence(
  avgStrokes: number,
  avgSeconds: number,
  indexed: number,
  total: number,
  openWaterSwimCount: number,
): string {
  if (indexed === 0) {
    // Open-water-only history: the trend counts pool lengths, so telling
    // someone who just swam 2 km of open-water freestyle "log some freestyle"
    // is misleading (persona-gate class: reasonable person misled). Name the
    // real rule instead.
    if (total === 0 && openWaterSwimCount > 0) {
      return "Your stroke trend builds from pool swims. Open water swims aren't counted here.";
    }
    return total > 0
      ? "We saw your swims. Once you log some freestyle, your stroke trend fills in here."
      : "Record a freestyle swim to start building your stroke trend.";
  }
  // NUMBER-FREE by design (persona gate round 4, B2, 5/5): the labeled stat
  // block below the chart owns the figures ("15 strokes per 25" and a "27s
  // per 25" chip; the head's chip owns N). When this sentence also
  // stated 15/27/N, the card printed the same numbers twice and read as two
  // merged drafts. This line is now just the topic lead-in. avgStrokes,
  // avgSeconds, indexed intentionally unused here.
  void avgStrokes;
  void avgSeconds;
  return "Your freestyle stroke count and time per length, averaged across your recent swims.";
}
